import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserEntity } from '@data/index';
import { useOrganization, useOrganizationChapters } from '@data/hooks/organization';
import { useSetDefaultChapter } from '@data/hooks/membership';
import { useOrganizationId } from '@app/utils/hooks';
import { MultiSelectField } from '@ui/components/ui/multi-select';

// General preferences: one-click settings that don't need a page of their own. Today that is the
// chapter your daily reports are filed under, which is an employee's setting — a client has no
// chapter, so for them this section is hidden.
const GeneralSection = ({ user, isEmployee }: { user: UserEntity; isEmployee: boolean }): JSX.Element => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const { data: chapters } = useOrganizationChapters(isEmployee ? organizationId : undefined, {
    includeDisabled: true,
  });
  // Your own membership in this organization: chapters belong to an organization, so the default does
  // too, and the same person can be back-end in one and design in another.
  const { data: organization } = useOrganization(isEmployee ? organizationId : undefined);
  const membership = (organization?.memberships ?? []).find(candidate => candidate.user?.id === user.id);
  // The own-membership route, not the admin patch. This section is every employee's own settings, and the
  // admin patch answers 403 to anyone who is not an admin — so for most of the people this is for, saving
  // a chapter here never landed.
  const { mutate: setDefaultChapter } = useSetDefaultChapter(membership?.id);

  // Held locally so the picker answers the click, and seeded from the server once it arrives.
  const [picked, setPicked] = useState<string | undefined>();
  const chapter = picked ?? membership?.defaultChapter?.key ?? '';
  const options = useMemo(() => (chapters?.items ?? []).map(c => ({ value: c.role, label: c.text })), [chapters]);

  const choose = (role: string): void => {
    if (!membership) return;
    setPicked(role);
    setDefaultChapter(
      role || null,
      // A refused save must not leave the picker showing a choice the server never took; dropping the
      // local pick falls back to what the server holds. The failure itself toasts globally.
      { onError: () => setPicked(undefined) },
    );
  };

  return (
    <div className="space-y-6">
      {isEmployee && (
        <section>
          <h3 className="text-sm font-medium text-foreground">{t('settings.general.yourChapter')}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.general.yourChapterHint')}</p>
          <div className="mt-3">
            <MultiSelectField
              single
              label={<span className="sr-only">{t('settings.general.yourChapter')}</span>}
              options={options}
              value={chapter ? [chapter] : []}
              onChange={vals => choose(vals[0] ?? '')}
              emptyLabel={t('settings.general.noChapter')}
              searchable={false}
              className="max-w-xs"
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default GeneralSection;
